import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import {
  Observable,
  switchMap,
  map,
  catchError,
  of,
  from,
  combineLatest
} from 'rxjs';
import * as fromCalendarActions from '../actions/calendar.actions';
import {
  CalendarEvent,
  CalendarEventFirebaseResponse
} from 'src/app/models/calendar.model';
import {
  AngularFirestore,
  DocumentReference
} from '@angular/fire/compat/firestore';
import { FirebaseService } from 'src/app/services/firebase/firebase.service';

@Injectable()
export class CalendarEffects {
  private readonly collectionName = 'events';
  constructor(
    private readonly action$: Actions,
    private readonly firebaseService: FirebaseService,
    private readonly firestore: AngularFirestore
  ) {}

  // Load Calendar
  loadCalendar$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromCalendarActions.LoadEventsAction),
      map(({ trainerId }) => {
        const trainerRef = this.firestore.doc(`users/${trainerId}`).ref;
        return this.firebaseService
          .getDataOfCollectionByAnyField(
            this.collectionName,
            'trainerRef',
            '==',
            trainerRef
          )
          .pipe(
            switchMap((resp) => {
              const events = resp.docs.map((doc) => ({
                ...(doc.data() as CalendarEventFirebaseResponse),
                id: doc.id
              }));
              return this.addUsersToEvent(events);
            })
          );
      }),
      switchMap((resp) => resp),
      map((calendarEvents) => ({
        type: fromCalendarActions.LOAD_EVENTS_SUCCESS,
        data: calendarEvents
      })),
      catchError((error) =>
        of({ type: fromCalendarActions.LOAD_EVENTS_FAIL, error })
      )
    )
  );

  // Add Event
  addEvent$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromCalendarActions.AddEventAction),
      switchMap(async ({ calendarEvent, trainerId }) => {
        const trainerRef = this.firestore.doc(`users/${trainerId}`).ref;

        const calendarEventToAdd = {
          ...calendarEvent,
          trainerRef,
          attendees: calendarEvent.attendees.map(
            (attendee) => this.firestore.doc(`users/${attendee.id}`).ref
          )
        };
        const result = await this.firebaseService.addDoc(
          this.collectionName,
          calendarEventToAdd
        );
        return { id: result.id, ...calendarEvent };
      }),
      map((calendarEvent) => ({
        type: fromCalendarActions.ADD_EVENT_SUCCESS,
        calendarEvent
      })),
      catchError((error) =>
        of({ type: fromCalendarActions.ADD_EVENT_FAIL, error })
      )
    )
  );

  // Remove Event
  removeEvent$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromCalendarActions.RemoveEventAction),
      switchMap(async ({ id }) => {
        await this.firebaseService.deleteDoc(this.collectionName, id);
        return id;
      }),
      map((id) => ({
        type: fromCalendarActions.REMOVE_EVENT_SUCCESS,
        id
      })),
      catchError((error) =>
        of({ type: fromCalendarActions.REMOVE_EVENT_FAIL, error })
      )
    )
  );

  // Update Event
  updateEvent$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromCalendarActions.UpdateEventAction),
      switchMap(async ({ calendarEvent, trainerId }) => {
        const trainerRef = this.firestore.doc(`users/${trainerId}`).ref;

        const calendarEventToUpdate = {
          ...calendarEvent,
          trainerRef,
          attendees: calendarEvent.attendees.map(
            (attendee) => this.firestore.doc(`users/${attendee.id}`).ref
          )
        };
        await this.firebaseService.updateDoc(
          this.collectionName,
          calendarEvent.id!,
          calendarEventToUpdate
        );
        return calendarEvent;
      }),
      map((calendarEvent) => ({
        type: fromCalendarActions.UPDATE_EVENT_SUCCESS,
        calendarEvent
      })),
      catchError((error) =>
        of({ type: fromCalendarActions.UPDATE_EVENT_FAIL, error })
      )
    )
  );

  private addUsersToEvent(
    events: CalendarEventFirebaseResponse[]
  ): Observable<CalendarEvent[]> {
    if (events?.length > 0) {
      let userRefs: DocumentReference[] = [];
      events.forEach((event) => {
        userRefs = [...userRefs, ...event.attendees];
      });
      const userObservables = userRefs.map((userRef) => from(userRef.get()));

      return combineLatest(userObservables).pipe(
        map((users) =>
          events.map((event) => {
            const attendees = event.attendees
              .map((attendee) => ({
                ...users.find((user) => user.id === attendee.id)!.data(),
                id: attendee.id
              }))
              .map((user: any) => {
                delete user.trainerRef;
                delete user.teamRef;
                return user;
              });

            delete event.trainerRef;

            return {
              ...event,
              attendees,
              start: new Date(event.start.seconds * 1000),
              end: new Date(event.end.seconds * 1000)
            } as CalendarEvent;
          })
        )
      );
    }
    return of([]);
  }
}
